import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Star, Facebook, Instagram, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSiteSettings } from '@/hooks/useSiteData';
import { useAuth } from '@/hooks/useAuth';
import { getUserPoints } from '@/hooks/useLoyalty';
import { CartSheet } from '@/components/cart/CartSheet';
import logo from '@/assets/logo.png';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/chi-sono', label: 'Chi Sono' },
  { href: '/massaggi', label: 'Massaggi' },
  { href: '/tantra', label: 'Tantra' },
  { href: '/corsi', label: 'Corsi' },
  { href: '/eventi', label: 'Eventi' },
  { href: '/gift-card', label: 'Gift Card' },
  { href: '/contatti', label: 'Contatti' },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const location = useLocation();
  const { data: settings } = useSiteSettings();
  const { user, isAdmin } = useAuth();

  // Fetch user points when logged in
  useEffect(() => {
    if (user) {
      getUserPoints(user.id).then((data) => {
        setUserPoints(data.points);
      });
    } else {
      setUserPoints(0);
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <nav 
      className={`font-urbanist fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-background/95 backdrop-blur-xl border-b border-border shadow-elegant' 
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo with hover animation - BIGGER */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group transition-transform duration-300 hover:scale-105 mt-10 md:mt-14 lg:mt-16"
          >
            <img 
              src={settings?.logo_url || logo} 
              alt={settings?.site_name || 'Karmaio'} 
              width={150}
              height={170}
              fetchPriority="high"
              decoding="async"
              className="h-20 md:h-32 lg:h-36 w-auto object-contain transition-all duration-300 group-hover:brightness-110 group-hover:drop-shadow-[0_0_20px_hsl(8_85%_55%/0.5)] mx-0 md:mx-[30px]"
            />

          </Link>

          {/* Desktop Navigation with ultra-modern animations */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative px-4 py-3 min-h-[44px] inline-flex items-center text-sm font-medium transition-all duration-300 group overflow-hidden rounded-lg ${
                  location.pathname === link.href
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Background hover effect */}
                <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-all duration-300 rounded-lg" />
                
                {/* Underline animation */}
                <span 
                  className={`absolute bottom-1 left-4 right-4 h-0.5 bg-primary transform origin-left transition-transform duration-300 ease-out ${
                    location.pathname === link.href ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
                
                {/* Glow effect on active */}
                {location.pathname === link.href && (
                  <span className="absolute inset-0 bg-primary/5 rounded-lg glow-pulse" />
                )}
                
                <span className="relative z-10">{link.label}</span>
              </Link>
            ))}
            
            
            {/* User Area with Points Badge */}
            <Link to={user ? "/area-personale" : "/auth"} className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px] ml-2" aria-label={user ? "Area personale" : "Accedi al tuo account"}>
              <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={user ? "Area personale" : "Accedi"} tabIndex={-1}>
                <User className="h-5 w-5" />
              </Button>
              {user && userPoints > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-[10px] flex items-center gap-0.5">
                  <Star className="h-2.5 w-2.5" />
                  {userPoints}
                </Badge>
              )}
            </Link>

            {/* Admin gear (admins only) */}
            {isAdmin && (
              <Link
                to="/admin"
                className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px]"
                aria-label="Area Admin"
                title="Area Admin"
              >
                <Button variant="ghost" size="icon" className="h-11 w-11 text-primary" tabIndex={-1}>
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>
            )}
            
            {/* Social icons */}
            {(settings?.facebook_url || settings?.instagram_url) && (
              <div className="flex items-center gap-1 ml-2 pl-3 border-l border-border/50">
                {settings?.facebook_url && (
                  <a
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300"
                  >
                    <Facebook className="h-4 w-4" />
                  </a>
                )}
                {settings?.instagram_url && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300"
                  >
                    <Instagram className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}

            {/* Cart with special styling */}
            <div className="ml-2 pl-4 border-l border-border/50">
              <CartSheet />
            </div>
          </div>

          {/* Mobile actions: Cart + Hamburger */}
          <div className="flex items-center gap-1 lg:hidden">
            <CartSheet />
            <Button
              variant="ghost"
              size="icon"
              className="relative overflow-hidden group h-11 w-11"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Menu"
            >
              <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-all duration-300 rounded-lg" />
              <div className="relative transition-transform duration-300">
                {isOpen ? (
                  <X className="h-8 w-8 animate-[spin_0.3s_ease-out]" strokeWidth={3} />
                ) : (
                  <Menu className="h-8 w-8" strokeWidth={3} />
                )}
              </div>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation with slide animation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-out ${
            isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 border-t border-border/50 bg-background/95 backdrop-blur-xl -mx-4 px-4">
            <div className="flex flex-col gap-1 stagger-children">
              {navLinks.map((link, index) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 overflow-hidden group ${
                    location.pathname === link.href
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  style={{ animationDelay: `${index * 70}ms` }}
                >
                  {/* Slide-in bar for active state */}
                  <span 
                    className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full transition-all duration-300 ${
                      location.pathname === link.href ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <span className="relative z-10">{link.label}</span>
              </Link>
              ))}
              
              
              {/* Mobile User Area */}
              <Link to={user ? "/area-personale" : "/auth"} className="mt-2">
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {user ? 'Area Personale' : 'Accedi'}
                  </span>
                  {user && userPoints > 0 && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {userPoints} punti
                    </Badge>
                  )}
                </Button>
              </Link>

              {isAdmin && (
                <Link to="/admin" className="mt-2">
                  <Button variant="outline" className="w-full justify-start gap-2 border-primary/40 text-primary">
                    <Settings className="h-4 w-4" />
                    Area Admin
                  </Button>
                </Link>
              )}
              
              
              {/* Mobile Social icons */}
              {(settings?.facebook_url || settings?.instagram_url) && (
                <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-border/50">
                  {settings?.facebook_url && (
                    <a
                      href={settings.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="p-3 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {settings?.instagram_url && (
                    <a
                      href={settings.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="p-3 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
